import React from "react";

import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import {
  Drawer as MuiDrawer,
  ListItemButton,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";

import styled from "@emotion/styled";
import { TFunction } from "i18next";

import { SystemData } from "globalTypes";
import { SidebarItemsType } from "types/sidebar";

import SidebarNav from "./SidebarNav";

export type SidebarProps = {
  PaperProps: {
    style: {
      width: number;
    };
  };
  variant?: "permanent" | "persistent" | "temporary";
  open?: boolean;
  onClose?: () => void;
  items: {
    title: string;
    pages: SidebarItemsType[];
  }[];
  systemData: SystemData;
  showFooter?: boolean;
  logo?: string;
  alt?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
};

type LogoSectionProps = {
  logo?: string;
  alt?: string;
};

type InvalidImageProps = {
  t: TFunction;
};

const InvalidImage = (props: InvalidImageProps) => {
  const { t } = props;

  return (
    <BrandIconFallback>
      <ImageNotSupportedIcon />
      <Typography variant="h6">{t("sidebar.common.noBranIconMsg")}</Typography>
    </BrandIconFallback>
  );
};

const LogoSection = (props: LogoSectionProps) => {
  const { t } = useTranslation();

  if (!props.logo) return <InvalidImage t={t} />;
  return (
    <BrandIcon
      src={props.logo}
      alt={props.alt ?? t("sidebar.common.noBrandIconAlt")}
    />
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  items,
  logo,
  alt,
  systemData,
  isCollapsed = false,
  onToggleCollapse,
  ...rest
}) => {
  const { t } = useTranslation();

  return (
    <Drawer variant="permanent" {...rest}>
      <Brand component={NavLink as any} to="/" isCollapsed={isCollapsed}>
        {isCollapsed ? (
          <BrandIconSmall
            src={logo ?? "/favicon.ico"}
            alt={alt ?? t("sidebar.common.noBrandIconAlt")}
          />
        ) : (
          <LogoSection logo={logo} alt={alt} />
        )}
      </Brand>
      <SidebarNav
        items={items}
        systemData={systemData}
        isCollapsed={isCollapsed}
      />
      {onToggleCollapse && (
        <Tooltip
          title={
            isCollapsed
              ? t("sidebar.common.expandSidebar")
              : t("sidebar.common.collapseSidebar")
          }
          placement="right"
          arrow
        >
          <CollapseButton onClick={onToggleCollapse} isCollapsed={isCollapsed}>
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </CollapseButton>
        </Tooltip>
      )}
    </Drawer>
  );
};

export default Sidebar;

const Drawer = styled(MuiDrawer)`
  border-right: 0;
  transition: width 0.3s ease-in-out;

  > div {
    border-right: 0;
    transition: width 0.3s ease-in-out;
  }
`;

const Brand = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})<{
  component?: React.ReactNode;
  to?: string;
  isCollapsed?: boolean;
}>`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  background-color: ${(props) => props.theme.sidebar.header.background};
  font-family: ${(props) => props.theme.typography.fontFamily};
  height: 56px;
  padding-left: ${(props) => props.theme.spacing(props.isCollapsed ? 2 : 6)};
  padding-right: ${(props) => props.theme.spacing(props.isCollapsed ? 2 : 6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;

  ${(props) => props.theme.breakpoints.up("sm")} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${(props) => props.theme.sidebar.header.background};
  }
`;

export const CollapseIconBase = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})<{ isCollapsed?: boolean }>(({ theme, isCollapsed }) => ({
  borderRadius: 999,
  padding: theme.spacing(1.25),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(15, 23, 42, 0.9)"
      : "rgba(255, 255, 255, 0.9)",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[6],
  backdropFilter: "blur(8px)",

  color: theme.palette.text.secondary,

  transition:
    "transform 0.18s ease-out, box-shadow 0.18s ease-out, background-color 0.18s ease-out, border-color 0.18s ease-out",

  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(30, 64, 175, 0.95)"
        : theme.palette.background.paper,
    boxShadow: theme.shadows[10],
    transform: isCollapsed ? "translateX(1px)" : "translateX(-1px)",
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(129, 140, 248, 0.8)"
        : theme.palette.primary.light,
    color:
      theme.palette.mode === "dark"
        ? theme.palette.common.white
        : theme.palette.primary.main,
  },

  "&:active": {
    boxShadow: theme.shadows[3],
    transform: "translateY(0)",
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },

  "& svg": {
    fontSize: "1.1rem",
  },
}));

const CollapseButton = styled(CollapseIconBase)<{ isCollapsed?: boolean }>(
  ({ theme, isCollapsed }) => ({
    position: "fixed",
    bottom: theme.spacing(3),
    left: isCollapsed ? 48 : 242,
    zIndex: 1300,
    transition: "left 0.3s ease-in-out",
  }),
);

export const InlineCollapseButton = styled(CollapseIconBase)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

export const EdgeExpandButton = styled(CollapseIconBase)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const BrandIcon = styled.img`
  margin-right: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.sidebar.header.brand.color};
  fill: ${(props) => props.theme.sidebar.header.brand.color};
  height: 50px;
`;

const BrandIconSmall = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const BrandIconFallback = styled(Box)`
  display: flex;
  column-gap: 0.5rem;
  align-items: center;
`;
